import styled from '@emotion/styled';
import { AppUI } from '@seedlang/state';
import autobind from 'autobind-decorator';
import InPlaceCheckbox from 'components/form/in_place_checkbox';
import InPlaceDatePicker from 'components/form/in_place_date_picker';
import InPlaceFileUpload from 'components/form/in_place_file_upload';
import InPlaceSelect from 'components/form/in_place_select';
import InPlaceText from 'components/form/in_place_text';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import Spinner from 'components/spinner';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { Link } from 'react-router';
import PodcastEpisodePublicationStateBanner from './podcast_episode_publication_state_banner';
import { RefreshIcon } from '@seedlang/icons';
import { isBlank, niceJoin } from '@seedlang/utils';
import Stack from 'components/layout/stack';
import Grid from 'components/layout/grid';
import GrayButton from 'components/button/gray_button';

const Wrapper = styled.div`
  @keyframes slide {
    0% { transform: translateX(-150px); }
    100% { transform: translateX(150px); }
  }
`;

const TextWithIconWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  .icon {
    width: 10px;
    height: 10px;
  }
`;

function UploadProgressBar({ progress }) {
  if (progress === 0) return null;
  const text = progress === 100 ? 'Processing...' : 'Uploading...';
  const innerDivStyle = progress === 100 ?
  {
    background: `repeating-linear-gradient(90deg, ${AppUI.site.accentColor} 0px, ${AppUI.site.accentColor} 150px, white 150px, white 300px)`,
    height: '100%',
    width: 'calc(100% + 300px)',
    animation: 'slide 0.8s linear infinite',
  } : {
    backgroundColor: AppUI.site.accentColor,
    height: '100%',
    width: `${progress}%`,
    transition: 'width 0.3s ease',
  };

  return (
    <div style={{marginTop: '10px'}}>
      <div style={{marginBottom: '5px'}}>{text}</div>
        <div style={{
          backgroundColor: 'white',
          height: '5px',
          position: 'relative',
          border: '1px solid #ccc',
          overflow: 'hidden',
        }}
        >
          <div style={innerDivStyle}/>
        </div>
      </div>
    );
}

@observer
class PodcastEpisodeEdit extends React.Component {
  @observable showSpinner = false;
  @observable showCreatePostSpinner = false;
  @observable uploadProgress = 0;

  constructor(props) {
    super(props);
    this.getPodcastEpisode();
    AppUI.podcastStore.getIndex();
    AppUI.podcastEpisodeStore.clearIndexData();
    AppUI.membershipTypeStore.getIndex({});
    window.a = AppUI;

  }

  @autobind async getPodcastEpisode() {
    this.showSpinner = true;
    await AppUI.podcastEpisodeStore.getShowPromise({ids: {podcastEpisodeId: this.props.params.podcastEpisodeId}});
    this.showSpinner = false;
    this.showCreatePostSpinner = false;
  }

  @autobind onUpdateForInactiveMembers() {
    AppUI.podcastEpisodeStore.showData.set('forMembersWithoutSubscription', !AppUI.podcastEpisodeStore.showData.forMembersWithoutSubscription);
    AppUI.podcastEpisodeStore.update({ids: {podcastEpisodeId: this.props.params.podcastEpisodeId}, data: {forMembersWithoutSubscription: AppUI.podcastEpisodeStore.showData.forMembersWithoutSubscription}}, this.getPodcastEpisode);
  }

  @autobind onUpdateRssFeeds() {
    this.showSpinner = true;
    AppUI.podcastStore.updateRssFeeds({ids: {podcastId: AppUI.podcastEpisodeStore.showData.podcast.id}}, this.getPodcastEpisode);
  }

  @autobind onCreateWorksheet() {
    this.showCreatePostSpinner = true;
    AppUI.worksheetStore.create({data: {podcast_episode_id: AppUI.podcastEpisodeStore.showData.id}}, this.getPodcastEpisode);
  }

  @computed get missingFieldsMessage() {
    const podcastEpisode = AppUI.podcastEpisodeStore.showData;
    if (!podcastEpisode) return '';

    const missing = [];
    if (isBlank(podcastEpisode.title)) missing.push('title');
    if (isBlank(podcastEpisode.description)) missing.push('description');
    if (isBlank(podcastEpisode.podcastId)) missing.push('podcast');
    if (isBlank(podcastEpisode.publishedAt)) missing.push('publication date');
    if (isBlank(podcastEpisode.mp3)) missing.push('mp3');

    return missing.length > 0 && `set ${niceJoin(missing)} to publish`;
  }

  @autobind onMp3UploadProgress(event) {
    if (event.total) {
      this.uploadProgress = Math.round((event.loaded / event.total) * 100);
    }
  }

  @autobind onMp3UploadComplete() {
    this.uploadProgress = 0;
    this.getPodcastEpisode();
  }

  render() {
    return (
      <Wrapper>
        <Stack gap='sm' alignItems='stretch'>
          <div className='breadcrumbs'>
            <Link
              to='creator.podcast_episodes.index'
            >
              View Podcast Episodes
            </Link>
          </div>
          <PodcastEpisodePublicationStateBanner
            publicationState={AppUI.podcastEpisodeStore.showDataField('publicationState')}
            publishedAt={AppUI.podcastEpisodeStore.showDataField('publishedAt')}
            onPublishChanges={this.onUpdateRssFeeds}
            showSpinner={this.showSpinner}
            missingFieldsMessage={this.missingFieldsMessage}
          />
          <Grid columns={2}>
              <fieldset>
                <legend>Title</legend>
                <InPlaceText
                  field='title'
                  defaultValue={AppUI.podcastEpisodeStore.showDataField('title')}
                  model='podcast_episodes'
                  id={AppUI.podcastEpisodeStore.showDataField('id')}
                  afterChange={this.getPodcastEpisode}
                />
              </fieldset>
              <fieldset>
                <legend>Subtitle</legend>
                <InPlaceText
                  field='subtitle'
                  defaultValue={AppUI.podcastEpisodeStore.showDataField('subtitle')}
                  model='podcast_episodes'
                  id={AppUI.podcastEpisodeStore.showDataField('id')}
                  afterChange={this.getPodcastEpisode}
                />
              </fieldset>
              <fieldset>
                <legend>Podcast</legend>
                <InPlaceSelect
                  includeBlank
                  field='podcast_id'
                  value={AppUI.podcastEpisodeStore.showDataField('podcastId')}
                  options={AppUI.podcastStore.indexData.map(item => [item.id, item.title])}
                  model='podcast_episodes'
                  id={AppUI.podcastEpisodeStore.showDataField('id')}
                  afterChange={this.getPodcastEpisode}
                />
              </fieldset>
              <fieldset>
                <legend>Publish(ed) At</legend>
                <InPlaceDatePicker
                  model='podcast_episodes'
                  field='published_at'
                  id={AppUI.podcastEpisodeStore.showData.id}
                  value={AppUI.podcastEpisodeStore.showData.publishedAt}
                  afterChange={this.getPodcastEpisode}
                  withTime
                  timeIntervals={60}
                />
              </fieldset>
            {
              AppUI.podcastEpisodeStore.hasShowData &&
                <>
                  <fieldset>
                    <legend>Access</legend>
                    <div style={{marginTop: 10}}>
                      <InPlaceCheckbox
                        value={AppUI.podcastEpisodeStore.showData.forMembersWithoutSubscription}
                        onChange={() => this.onUpdateForInactiveMembers()}
                      >
                        For Inactive Members Only
                      </InPlaceCheckbox>
                    </div>
                  </fieldset>
                  <fieldset>
                    <legend>Interactive Player Link</legend>
                    <InPlaceText
                      field='interactive_player_link'
                      defaultValue={AppUI.podcastEpisodeStore.showDataField('interactivePlayerLink')}
                      model='podcast_episodes'
                      id={AppUI.podcastEpisodeStore.showDataField('id')}
                      afterChange={this.getPodcastEpisode}
                    />
                  </fieldset>
                </>
            }
          </Grid>
          <InPlaceText
            legend='Description (HTML)'
            richText
            defaultValue={AppUI.podcastEpisodeStore.showData.description}
            inputType='textarea'
            model='podcast_episodes'
            field='description'
            id={AppUI.podcastEpisodeStore.showData.id}
            afterChange={this.getPodcastEpisode}
            maxHeight='30em'
            scrollable
          />
          <fieldset>
            <legend>MP3 (Max 100MB)</legend>
            <InPlaceFileUpload
              model='mp3s'
              file={AppUI.podcastEpisodeStore.showData.mp3}
              id={AppUI.podcastEpisodeStore.showData.id}
              successCallback={this.onMp3UploadComplete}
              progressCallback={this.onMp3UploadProgress}
              querystring={{
                mp3able_id: AppUI.podcastEpisodeStore.showData.id,
                mp3able_type: 'PodcastEpisode',
              }}
              showSpinner={false}
            />
            <UploadProgressBar progress={this.uploadProgress} />
          </fieldset>
          <fieldset>
            <legend>Subtitles (.vtt or .srt)</legend>
            <InPlaceFileUpload
              model='subtitle_files'
              file={AppUI.podcastEpisodeStore.showData.subtitleFile}
              id={AppUI.podcastEpisodeStore.showData.id}
              successCallback={this.getPodcastEpisode}
              querystring={{
                subtitlable_id: AppUI.podcastEpisodeStore.showData.id,
                subtitlable_type: 'PodcastEpisode',
              }}
            />
          </fieldset>
          <fieldset>
            <legend>Link to Post</legend>
            {
              AppUI.podcastEpisodeStore.showDataField('worksheetId') ? (
                // The `link` is autogenerated on the backend in PodcastEpisode model when there is an associated `worksheet`
                <>
                  <InPlaceText
                    field='link'
                    defaultValue={AppUI.podcastEpisodeStore.showDataField('link')}
                    model='podcast_episodes'
                    id={AppUI.podcastEpisodeStore.showDataField('id')}
                    readOnly
                  />
                  <Link to={{ name: 'creator.worksheets.edit', params: { worksheetId: AppUI.podcastEpisodeStore.showDataField('worksheetId') } }}>
                    <GrayButton>
                      <i className='fa fa-angle-right' />
                      Go to Post
                    </GrayButton>
                  </Link>

                </>
              ) : (
                <>
                  {
                    this.showCreatePostSpinner ? (
                      <Spinner />
                    ) : (
                      <GrayButton onClick={this.onCreateWorksheet}>
                        <i className='fa fa-plus' />
                        Create Post
                      </GrayButton>
                    )
                  }
                </>
              )
            }
          </fieldset>
          {
            AppUI.user.developer && AppUI.podcastEpisodeStore.hasShowData && AppUI.podcastEpisodeStore.showData.podcast &&
              <div>
                  <fieldset>
                    <legend>Developer Tools: RSS Feeds - {AppUI.podcastEpisodeStore.showData.podcast.title}</legend>
                    <div>
                      Last Updated: {AppUI.podcastEpisodeStore.showData.podcast.rssUpdatedAt ? AppUI.podcastEpisodeStore.showData.podcast.rssUpdatedAt.formatted : 'never'}
                    </div>
                    <TextWithIconWrapper>
                      Pending RSS update jobs: {AppUI.podcastEpisodeStore.showData.podcast.rssUpdateNeededCount}
                      <RefreshIcon onClick={this.getPodcastEpisode} />
                    </TextWithIconWrapper>
                    {
                      !this.showSpinner &&
                        <GrayButton
                          style={{marginTop: '10px'}}
                          onClick={this.onUpdateRssFeeds}
                        >
                          Update RSS Feeds
                        </GrayButton>
                    }
                    {
                      this.showSpinner &&
                        <Spinner />
                    }
                  </fieldset>
              </div>
          }
        </Stack>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(PodcastEpisodeEdit);
